import type {
  CommandPaletteLabels,
  ComposerLabels,
  ContextUsageLabels,
  PopupLabels,
  PreviewPanelLabels,
  SidebarFolderLabels,
  SidebarNavLabels,
  UsageIndicatorLabels,
} from "@design";
import { useTranslation } from "react-i18next";

/** Translated built-in strings for the design system's SidebarNav. */
export function useSidebarNavLabels(): SidebarNavLabels {
  const { t } = useTranslation("shell", { keyPrefix: "sidebar" });
  return {
    expand: t("expand"),
    expandNavigation: t("expandNavigation"),
    collapse: t("collapse"),
    collapseNavigation: t("collapseNavigation"),
    search: t("search"),
    newChat: t("newChat"),
    home: t("home"),
    pinnedSpaces: t("pinnedSpaces"),
    newSpace: t("newSpace"),
    nothingPinned: t("nothingPinned"),
    unpin: t("unpin"),
    allSpaces: t("allSpaces"),
    build: t("build"),
    admin: t("admin"),
    notifications: t("notifications"),
    settings: t("settings"),
    help: t("help"),
    feedback: t("feedback"),
    account: t("account"),
    showSpaces: t("showSpaces"),
    hideSpaces: t("hideSpaces"),
    emptyFolder: t("emptyFolder"),
    star: t("star"),
  };
}

/**
 * Translated strings for the sidebar's space folders. The design system fills in `{{name}}` / `{{target}}`
 * itself, so they're passed through here rather than interpolated by i18next.
 */
export function useSidebarFolderLabels(): SidebarFolderLabels {
  const { t } = useTranslation("shell", { keyPrefix: "sidebar.folders" });
  const keep = { name: "{{name}}", target: "{{target}}" };
  return {
    newFolder: t("newFolder"),
    folderName: t("folderName"),
    folderNamePlaceholder: t("folderNamePlaceholder"),
    folderMenu: t("folderMenu"),
    spaceMenu: t("spaceMenu"),
    folderMenuAria: t("folderMenuAria", keep),
    spaceMenuAria: t("spaceMenuAria", keep),
    spaceSettings: t("spaceSettings"),
    rename: t("rename"),
    removeFolder: t("removeFolder"),
    sortCustom: t("sortCustom"),
    sortAlpha: t("sortAlpha"),
    sortRecent: t("sortRecent"),
    moveTo: t("moveTo", keep),
    moveToNewFolder: t("moveToNewFolder"),
    removeFromFolder: t("removeFromFolder"),
    moveUp: t("moveUp"),
    moveDown: t("moveDown"),
    moveGroup: t("moveGroup"),
    moveToGroup: t("moveToGroup"),
    sortGroup: t("sortGroup"),
    customOrderTitle: t("customOrderTitle"),
    customOrderBody: t("customOrderBody", keep),
    customOrderConfirm: t("customOrderConfirm"),
    customOrderCancel: t("customOrderCancel"),
    viewAll: t("viewAll"),
    viewAllPage: t("viewAllPage"),
    searchSpaces: t("searchSpaces"),
    noResults: t("noResults"),
    noSpaces: t("noSpaces"),
    dragSpace: t("dragSpace", keep),
    dragFolder: t("dragFolder", keep),
    emptyFolder: t("emptyFolder"),
    dragInstructions: t("dragInstructions"),
    pickedUp: t("pickedUp", keep),
    movedOver: t("movedOver", keep),
    dropped: t("dropped", keep),
    cancelled: t("cancelled", keep),
  };
}

/** Translated built-in strings for the design system's Composer. */
export function useComposerLabels(): ComposerLabels {
  const { t } = useTranslation("chat", { keyPrefix: "composer" });
  return {
    posture: t("posture"),
    ask: t("ask"),
    goal: t("goal"),
    attach: t("attach"),
    assistant: t("assistant"),
    assistantNamed: (name) => t("assistantNamed", { name }),
    responseMode: t("responseMode"),
    responseModeNamed: (name) => t("responseModeNamed", { name }),
    recordAudio: t("recordAudio"),
    stopRecording: t("stopRecording"),
    moreOptions: t("moreOptions"),
    send: t("send"),
    stopGenerating: t("stopGenerating"),
  };
}

/** Translated built-in strings for the design system's CommandPalette. */
export function useCommandPaletteLabels(): CommandPaletteLabels {
  const { t } = useTranslation("shell", { keyPrefix: "palette" });
  return {
    dialog: t("dialog"),
    placeholder: t("placeholder"),
    escape: t("escape"),
    noMatches: (query) => t("noMatches", { query }),
    allCategories: t("allCategories"),
    pinned: t("pinned"),
    browse: t("browse"),
    pin: (label) => t("pin", { label }),
    unpin: (label) => t("unpin", { label }),
  };
}

/** Translated built-in strings for the design system's space Popup. */
export function usePopupLabels(): PopupLabels {
  const { t } = useTranslation("shell", { keyPrefix: "spacePopup" });
  return {
    overview: t("overview"),
    chats: t("chats"),
    knowledge: t("knowledge"),
    newChat: t("newChat"),
    quickActions: t("quickActions"),
    assistants: t("assistants"),
  };
}

/** Translated built-in strings for the design system's PreviewPanel. */
export function usePreviewPanelLabels(): PreviewPanelLabels {
  const { t } = useTranslation("chat", { keyPrefix: "documents.panel" });
  return {
    outputs: t("outputs"),
    close: t("close"),
    previous: t("previous"),
    next: t("next"),
    position: (current, total) => t("position", { current, total }),
    view: t("view"),
    expand: t("expand"),
    collapse: t("collapse"),
    resize: t("resize"),
  };
}

/** Translated built-in strings for the design system's ContextUsage meter. */
export function useContextUsageLabels(): ContextUsageLabels {
  const { t } = useTranslation("chat", { keyPrefix: "contextUsage" });
  return {
    tokens: (tokens) => t("tokens", { tokens }),
    percentUsed: (percent) => t("percentUsed", { percent }),
    pruned: (count) => t("pruned", { count }),
    trimmed: (count) => t("trimmed", { count }),
    summarized: t("summarized"),
    contextWindow: t("contextWindow"),
    maxOutput: t("maxOutput"),
    toolDefinitions: t("toolDefinitions"),
    availableBudget: t("availableBudget"),
    promptTokens: t("promptTokens"),
    messages: t("messages"),
    messagesLoaded: (count) => t("messagesLoaded", { count }),
    compactionPipeline: t("compactionPipeline"),
    groupsPruned: (count) => t("groupsPruned", { count }),
    groupsTrimmed: (count) => t("groupsTrimmed", { count }),
  };
}

/** Translated built-in strings for the design system's UsageIndicator (sidebar usage ring). */
export function useUsageIndicatorLabels(): UsageIndicatorLabels {
  const { t } = useTranslation("shell", { keyPrefix: "usage" });
  return {
    usage: t("usage"),
    monthlyBudget: t("monthlyBudget"),
    aiUsage: t("aiUsage"),
    aiUsageSince: (period) => t("aiUsageSince", { period }),
    cost: t("cost"),
    requests: t("requests"),
    tokensIn: t("tokensIn"),
    tokensOut: t("tokensOut"),
    viewAllUsage: t("viewAllUsage"),
  };
}
